import { defineStore } from "pinia";
import { ref } from "vue";
import axios from "axios";
import { useI18n } from "vue-i18n";

type SessionAction = "retry" | "logout";
type ModalMode = "conflict" | "rateLimit";

export const useSessionConflictStore = defineStore("sessionConflict", () => {
  const { t } = useI18n();

  const visible = ref(false);
  const loading = ref(false);
  const mode = ref<ModalMode>("conflict");
  const errorMessage = ref("");
  const emailInfoMessage = ref("");
  const resetErrorMessage = ref("");

  let resolveAction: ((action: SessionAction) => void) | null = null;
  let activePromise: Promise<SessionAction> | null = null;

  function showConflict(message: string): Promise<SessionAction> {
    if (visible.value && activePromise) {
      return activePromise;
    }

    mode.value = "conflict";
    errorMessage.value = message;
    emailInfoMessage.value = "";
    resetErrorMessage.value = "";
    visible.value = true;
    loading.value = false;

    activePromise = new Promise<SessionAction>((resolve) => {
      resolveAction = resolve;
    });

    return activePromise;
  }

  function showRateLimit(message: string): Promise<SessionAction> {
    if (visible.value && activePromise) {
      return activePromise;
    }

    mode.value = "rateLimit";
    errorMessage.value = message;
    visible.value = true;
    loading.value = false;

    activePromise = new Promise<SessionAction>((resolve) => {
      resolveAction = resolve;
    });

    return activePromise;
  }

  function handleRetry() {
    visible.value = false;
    activePromise = null;
    resolveAction?.("retry");
    resolveAction = null;
  }

  /**
   * «Завершить сеансы на всех устройствах». При единой авторизации это отзыв токенов
   * через LH, а не письмо со ссылкой сброса, как было в прежней схеме: логин с паролем
   * для него не нужны — человек уже подтверждён кукой.
   *
   * Отзыв гасит и ТЕКУЩУЮ сессию: остаться в кабинете после него нельзя, поэтому
   * уводим на страницу входа сразу, а не оставляем человека в кабинете, где ничего
   * не работает.
   */
  async function handleResetSessions() {
    loading.value = true;
    resetErrorMessage.value = "";
    try {
      const { useAuthStore } = await import("./authApi");
      const { message } = await useAuthStore().resetAllDevices();

      emailInfoMessage.value = message;
      window.location.assign("/login");
    } catch (error) {
      const msg = axios.isAxiosError(error)
        ? (error.response?.data?.error?.message ?? t("common.sessionResetError"))
        : t("common.sessionResetError");
      resetErrorMessage.value = msg;
    } finally {
      loading.value = false;
    }
  }

  function handleLogout() {
    visible.value = false;
    activePromise = null;
    resolveAction?.("logout");
    resolveAction = null;
  }

  return {
    visible,
    loading,
    mode,
    errorMessage,
    emailInfoMessage,
    resetErrorMessage,
    showConflict,
    showRateLimit,
    handleRetry,
    handleResetSessions,
    handleLogout,
  };
});
